import React, { useState, useEffect, useRef } from 'react';
import './CourseSelectionModal.css';
import { getAllCourses, getCredentials, insertMultipleCredentials } from '../services/api';



const CourseSelectionModal = ({ isOpen, onClose, onBack, onCredentialsGenerated, programName }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [credentialTitle, setCredentialTitle] = useState('');
  const [useRecommendedTitle, setUseRecommendedTitle] = useState(false);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsError, setCredentialsError] = useState(null);
  const [selectedCredentials, setSelectedCredentials] = useState([]);
  const [credentialCourseMapping, setCredentialCourseMapping] = useState({});
  const [isScrollable, setIsScrollable] = useState(false);
  const [coursesWithCredentials, setCoursesWithCredentials] = useState(new Set());
  const [credentialCheckLoading, setCredentialCheckLoading] = useState(false);
  
  const modalRef = useRef(null);
  const contentRef = useRef(null);

  // Check credentials for a single course
  const checkCourseCredentials = async (courseCode, school) => {
    try {
      const response = await getCredentials([courseCode]);
      // Check if the response has any credentials for this course
      const hasCredentials = response && Object.keys(response).length > 0 && 
        Object.values(response).some(credentialList => credentialList && credentialList.length > 0);
      return hasCredentials;
    } catch (err) {
      console.error(`Error checking credentials for course ${courseCode}:`, err);
      return false; // Assume no credentials if there's an error
    }
  };

  // Check credentials for all courses
  const checkAllCoursesCredentials = async (courses, school) => {
    setCredentialCheckLoading(true);
    const coursesWithCreds = new Set();
    
    // Check credentials for each course
    for (const course of courses) {
      const hasCredentials = await checkCourseCredentials(course.fullCode, school);
      if (hasCredentials) {
        coursesWithCreds.add(course.id);
      }
    }
    
    setCoursesWithCredentials(coursesWithCreds);
    setCredentialCheckLoading(false);
  };

  // Fetch courses when modal opens
  useEffect(() => {
    const fetchCourses = async () => {
      if (!isOpen) return;
      
      setLoading(true);
      setError(null);
      setCoursesWithCredentials(new Set()); // Reset credentials state
      
      try {
        // Get school from URL params or use default
        const urlParams = new URLSearchParams(window.location.search);
        const school = urlParams.get('school') || 'MARYLAND';
        
        const response = await getAllCourses(school, programName);
        
        console.log('API Response:', response); // Debug log to see structure
        
        // Handle the specific response structure: { course_list: ["BSM-BADM311", ...] }
        let courseCodes = [];
        if (response && response.course_list && Array.isArray(response.course_list)) {
          courseCodes = response.course_list;
        } else if (Array.isArray(response)) {
          courseCodes = response;
        } else {
          console.warn('Unexpected response structure:', response);
          courseCodes = [];
        }
        
        // Transform course codes to course objects - just display the raw course codes
        const transformedCourses = courseCodes.map((courseCode, index) => {
          return {
            id: index + 1,
            code: courseCode,
            title: '', // No title needed, just show the raw course code
            fullCode: courseCode
          };
        });
        
        setCourses(transformedCourses);
        
        // Check credentials for all courses
        if (transformedCourses.length > 0) {
          await checkAllCoursesCredentials(transformedCourses, school);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses. Please try again.');
        // Fallback to empty array
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [isOpen]);

  // Helper function to find union of credentials across all selected courses
  const findUnionCredentials = (credentialsResponse) => {
    if (!credentialsResponse || Object.keys(credentialsResponse).length === 0) {
      return {};
    }

    // Store the original relationship mapping for later use
    setCredentialCourseMapping(credentialsResponse);

    // Get all unique credentials from all courses (union)
    const allCredentials = new Set();
    Object.values(credentialsResponse).forEach(credentialList => {
      credentialList.forEach(credential => allCredentials.add(credential));
    });

    // Return all credentials that appear in ANY of the selected courses (union)
    const unionCredentials = {};
    unionCredentials['available'] = Array.from(allCredentials);

    return unionCredentials;
  };

  // Check if modal content is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (modalRef.current) {
        const modal = modalRef.current;
        const isContentScrollable = modal.scrollHeight > modal.clientHeight;
        setIsScrollable(isContentScrollable);
      }
    };

    if (isOpen) {
      // Check scrollable state after content loads
      const timer = setTimeout(checkScrollable, 100);
      
      // Add resize observer to detect content changes
      const resizeObserver = new ResizeObserver(checkScrollable);
      if (modalRef.current) {
        resizeObserver.observe(modalRef.current);
      }
      if (contentRef.current) {
        resizeObserver.observe(contentRef.current);
      }

      return () => {
        clearTimeout(timer);
        resizeObserver.disconnect();
      };
    }
  }, [isOpen, courses, credentials, useRecommendedTitle, selectedCourses]);

  // Fetch credentials when toggle is turned on and courses are selected
  useEffect(() => {
    const fetchCredentials = async () => {
      if (!useRecommendedTitle || selectedCourses.length === 0) {
        setCredentials({});
        setSelectedCredentials([]);
        return;
      }

      setCredentialsLoading(true);
      setCredentialsError(null);

      try {
        // Get the full course codes for selected courses
        const selectedCourseCodes = courses
          .filter(course => selectedCourses.includes(course.id))
          .map(course => course.fullCode);

        if (selectedCourseCodes.length > 0) {
          const response = await getCredentials(selectedCourseCodes);
          
          // Find union of credentials (all credentials from any selected course)
          const unionCredentials = findUnionCredentials(response);
          setCredentials(unionCredentials);
          setSelectedCredentials([]); // Reset selection when new credentials are fetched
        }
      } catch (err) {
        console.error('Error fetching credentials:', err);
        setCredentialsError('Failed to load credential recommendations. Please try again.');
        setCredentials({});
      } finally {
        setCredentialsLoading(false);
      }
    };

    fetchCredentials();
  }, [useRecommendedTitle, selectedCourses, courses]);

  if (!isOpen) return null;

  const filteredCourses = courses.filter(course =>
    course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCourseSelect = (courseId) => {
    // Don't allow selection of courses without credentials
    if (!coursesWithCredentials.has(courseId)) {
      return;
    }
    
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleGenerate = async () => {
    if (selectedCourses.length === 0) {
      setError('Please select at least one course.');
      return;
    }

    try {
      // Get school from URL params or use default
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';

      if (useRecommendedTitle) {
        if (selectedCredentials.length === 0) {
          setError('Please select at least one credential recommendation.');
          return;
        }

        // Build credential mapping based on actual relationships
        const credentialMapping = {};
        
        selectedCredentials.forEach(credential => {
          const coursesForCredential = [];
          
          // Find which selected courses actually offer this credential
          Object.entries(credentialCourseMapping).forEach(([courseCode, credentialList]) => {
            if (credentialList.includes(credential)) {
              coursesForCredential.push(courseCode);
            }
          });
          
          if (coursesForCredential.length > 0) {
            credentialMapping[credential] = coursesForCredential;
          }
        });

        if (Object.keys(credentialMapping).length === 0) {
          setError('No valid credential-course relationships found.');
          return;
        }

        // Make single API call with credential mapping
        const response = await insertMultipleCredentials(school, credentialMapping);
        
        console.log('Credentials inserted successfully:', {
          school: school,
          credentialMapping: credentialMapping,
          response: response
        });
        
        // Call the refresh callback to update the parent component
        if (onCredentialsGenerated) {
          onCredentialsGenerated();
        }
        
      } else {
        // Handle manual credential title input
        if (!credentialTitle.trim()) {
          setError('Please enter a credential title.');
          return;
        }

        // For manual input, associate with all selected courses
        const selectedCourseCodes = courses
          .filter(course => selectedCourses.includes(course.id))
          .map(course => course.fullCode);

        const credentialMapping = {
          [credentialTitle.trim()]: selectedCourseCodes
        };

        const response = await insertMultipleCredentials(school, credentialMapping);
        
        console.log('Credential inserted successfully:', {
          school: school,
          credentialMapping: credentialMapping,
          response: response
        });
        
        // Call the refresh callback to update the parent component
        if (onCredentialsGenerated) {
          onCredentialsGenerated();
        }
      }
      
      onClose();
    } catch (error) {
      console.error('Error inserting credentials:', error);
      setError('Failed to insert credentials. Please try again.');
    }
  };


  const isGenerateEnabled = selectedCourses.length > 0 && 
    (credentialTitle || (useRecommendedTitle && selectedCredentials.length > 0));

  return (
    <div className="course-selection-modal-overlay">
      <div className="course-selection-modal" ref={modalRef}>
        <div className="modal-content" ref={contentRef}>
        {/* Close Button */}
        <button className="course-modal-close-button" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className="course-modal-header">
          <div className="header-content">
            <h2 className="course-modal-title">Create Credential from Course(s)</h2>
            <p className="course-modal-description">
              Select course(s) that are related with the credential
              {selectedCourses.length > 0 && (
                <span className="selected-count"> ({selectedCourses.length} selected)</span>
              )}
            </p>
          </div>
        </div>

        {/* Course List */}
        <div className="course-list-container">
          {/* New Search Bar */}
          <div className="new-search-container">
            <svg className="search-magnifier-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              className="new-search-input"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Course List */}
          <div className="course-list-wrapper">
            <div className="course-list">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <span>Loading courses...</span>
                </div>
              ) : credentialCheckLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <span>Checking course credentials...</span>
                </div>
              ) : error ? (
                <div className="error-state">
                  <span>{error}</span>
                  <button 
                    className="retry-button" 
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      
                      try {
                        const urlParams = new URLSearchParams(window.location.search);
                        const school = urlParams.get('school') || 'MARYLAND';
                        const response = await getAllCourses(school);
                        
                        // Handle the specific response structure: { course_list: ["BSM-BADM311", ...] }
                        let courseCodes = [];
                        if (response && response.course_list && Array.isArray(response.course_list)) {
                          courseCodes = response.course_list;
                        } else if (Array.isArray(response)) {
                          courseCodes = response;
                        } else {
                          console.warn('Unexpected response structure:', response);
                          courseCodes = [];
                        }
                        
                        // Transform course codes to course objects - just display the raw course codes
                        const transformedCourses = courseCodes.map((courseCode, index) => {
                          return {
                            id: index + 1,
                            code: courseCode,
                            title: '', // No title needed, just show the raw course code
                            fullCode: courseCode
                          };
                        });
                        setCourses(transformedCourses);
                      } catch (err) {
                        console.error('Error fetching courses:', err);
                        setError('Failed to load courses. Please try again.');
                        setCourses([]);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : filteredCourses.length === 0 ? (
                <div className="no-courses-state">
                  <span>No courses found matching your search.</span>
                </div>
              ) : (
                filteredCourses.map((course) => {
                  const hasCredentials = coursesWithCredentials.has(course.id);
                  const isDisabled = !hasCredentials;
                  
                  return (
                    <div 
                      key={course.id} 
                      className={`course-item-credential ${selectedCourses.includes(course.id) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => handleCourseSelect(course.id)}
                    >
                      <div className="course-info">
                        <span className="course-title-credential">{course.code}</span>
                      </div>
                      <div className="course-checkbox">
                        <label className="checkbox-container">
                          <input
                            type="checkbox"
                            checked={selectedCourses.includes(course.id)}
                            onChange={() => handleCourseSelect(course.id)}
                            onClick={(e) => e.stopPropagation()} // Prevent double-triggering
                            disabled={isDisabled}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>


        </div>

        {/* Credential Information */}
        <div className="credential-info-section">
          <h3 className="credential-info-title">Credential Information</h3>
          
          <div className="credential-title-section">
            <label className="credential-label">Credential Title <span className="required-asterisk">*</span></label>
            <input
              type="text"
              placeholder="Please name the credential here"
              value={credentialTitle}
              onChange={(e) => setCredentialTitle(e.target.value)}
              className="credential-title-input"
              disabled={useRecommendedTitle}
            />
          </div>

          <div className="or-divider">OR</div>

          <div className="recommended-title-section">
            <div className="toggle-container">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={useRecommendedTitle}
                  onChange={(e) => setUseRecommendedTitle(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">Use Recommended Credential Title</span>
            </div>

            {/* Credential Recommendations */}
            {useRecommendedTitle && (
              <div className="course-selection-credentials-section">
                {credentialsLoading ? (
                  <div className="credentials-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading recommendations...</span>
                  </div>
                ) : credentialsError ? (
                  <div className="credentials-error">
                    <span>{credentialsError}</span>
                  </div>
                ) : selectedCourses.length === 0 ? (
                  <div className="credentials-help">
                    <span>Please select courses to see credential recommendations</span>
                  </div>
                ) : Object.keys(credentials).length === 0 ? (
                  <div className="credentials-help">
                    <span>No overlapping credential recommendations found for the selected courses</span>
                  </div>
                ) : (
                  <div className="credentials-list">
                    <div className="credentials-options">
                      {Object.entries(credentials).map(([category, credentialList]) =>
                        credentialList.map((credential, index) => (
                          <label 
                            key={`${category}-${index}`} 
                            className={`course-selection-credential-option ${selectedCredentials.includes(credential) ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              name="recommendedCredentials"
                              value={credential}
                              checked={selectedCredentials.includes(credential)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCredentials(prev => [...prev, credential]);
                                } else {
                                  setSelectedCredentials(prev => prev.filter(c => c !== credential));
                                }
                              }}
                            />
                            <span className="course-selection-credential-text">{credential}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        </div>
        
        {/* Action Buttons */}
        <div className={`course-action-buttons ${isScrollable ? 'sticky' : 'static'}`}>
          <button className="back-button" onClick={onBack}>
            Back
          </button>
          <button 
            className={`generate-button ${isGenerateEnabled ? 'enabled' : 'disabled'}`}
            onClick={handleGenerate}
            disabled={!isGenerateEnabled}
          >
            Generate
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseSelectionModal;
